import { put, takeEvery, all, call, race, delay, cancelled } from "redux-saga/effects";
import moment from "moment-timezone";
import { PICTREE, ENDPOINT } from "../../constants";
import {
  setPicTreeRegularSeasonPageData,
  setPicTreeRegularSeasonPageLoading,
  setPageTopLoader,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";

const REGULAR_SEASON_UI_TO_GAME_SPACE_ID = {
  "regular-season-1": "cmp4yz2lj00017rtcc4ok0jgv",
  "regular-season-2": "cmrz0wo8t0000bpzcana86bow",
  "regular-season-3": "cmunxinty000079zc6zf595vs",
};
const REGULAR_SEASON_GAME_SPACE_ID_TO_LABEL = {
  cmp4yz2lj00017rtcc4ok0jgv: "RS1",
  cmrz0wo8t0000bpzcana86bow: "RS2",
  cmunxinty000079zc6zf595vs: "RS3",
};

const REGULAR_SEASON_MAX_BY_GAME_SPACE_ID = {
  cmp4yz2lj00017rtcc4ok0jgv: 812206,
  cmrz0wo8t0000bpzcana86bow: 812206,
  cmunxinty000079zc6zf595vs: 812206,
};
const REGULAR_SEASON_SELECTION_ORDER = {
  cmp4yz2lj00017rtcc4ok0jgv: 0,
  cmrz0wo8t0000bpzcana86bow: 1,
  cmunxinty000079zc6zf595vs: 2,
};
const REGULAR_SEASON_OVERVIEW_SUMMARY_RDS_TABLE = "PicTreeKpiRegularSeasonOverviewSummaryProd";

const COMPANY_NAME_BY_CODE = {
  NTT_ME: "NTT ME",
  TEPCO: "東京電力 TEPCO",
  NTT_FT: "NTT FT",
  HEPCO: "ほくでん",
};

const COMPANY_SERIES_ORDER = ["NTT_ME", "TEPCO", "NTT_FT", "HEPCO"];
const REGULAR_SEASON_TREND_RDS_TABLE = "PicTreeKpiRegularSeasonDailyTrendAggProd";
const REGULAR_SEASON_COMPANY_ACHIEVE_RDS_TABLE = "PicTreeKpiRegularSeasonCompanyAchieveAggProd";
const REGULAR_SEASON_TREND_COMPANY_CODE = "NTT_ME";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,
  retries: 3,
  backoffBase: 1500,
};

function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,
    retries = 2,
    backoffBase = 1000,
    backoffFactor = 2,
    jitter = true,
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) {
        throw new Error(`timeout after ${timeoutMs}ms`);
      }
      return response;
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err;
      }

      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
    } finally {
      if (yield cancelled()) {
        // no-op
      }
    }
  }
}

const formatNumber = (value) => Number(value || 0).toLocaleString("ja-JP");

const resolveGameSpaceId = (selectedSeasonId) => (
  REGULAR_SEASON_UI_TO_GAME_SPACE_ID[selectedSeasonId] || selectedSeasonId || "cmunxinty000079zc6zf595vs"
);

const normalizeRegularSeasonGameSpaceIds = (selectedSeasonIds, selectedSeasonId) => {
  const uiSeasonIds = normalizeSelectionSeasonIds(selectedSeasonIds, selectedSeasonId);
  return Array.from(new Set(uiSeasonIds.map((seasonId) => resolveGameSpaceId(seasonId)).filter(Boolean)));
};

const sortRegularSeasonGameSpaceIds = (seasonIds) => Array.from(seasonIds || [])
  .slice()
  .sort((left, right) => (REGULAR_SEASON_SELECTION_ORDER[left] ?? Number.MAX_SAFE_INTEGER) - (REGULAR_SEASON_SELECTION_ORDER[right] ?? Number.MAX_SAFE_INTEGER));

const buildRegularSeasonSeasonChartQueries = ({ selectedSeasonId, selectedSeasonIds }) => {
  const seasonIds = normalizeRegularSeasonGameSpaceIds(selectedSeasonIds, selectedSeasonId);
  const sortedSeasonIds = sortRegularSeasonGameSpaceIds(seasonIds);

  if (!sortedSeasonIds.length) {
    return {
      seasonIds: [],
      seasonListQuery: null,
      seasonCountsQuery: null,
    };
  }

  const seasonIdList = sortedSeasonIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const seasonListQuery = {
    query: `
      SELECT
        gs.id AS season_id,
        sn.official_name AS season_name
      FROM game_space gs
      JOIN game_term gt ON gt.id = gs.game_term_id
      JOIN season_name sn ON sn.season_id = gs.id AND sn.language_code = 'JA'
      WHERE gs.id IN (${seasonIdList})
      ORDER BY gt.start_datetime ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const seasonCountsQuery = sortedSeasonIds.length > 1
    ? {
        query: `
          WITH season_priority AS (
            SELECT '${sortedSeasonIds[0]}' AS season_id, 1 AS season_rank
            UNION ALL
            SELECT '${sortedSeasonIds[1]}' AS season_id, 2 AS season_rank
          ),
          eligible AS (
            SELECT
              pae.power_asset_id,
              pae.game_space_id AS season_id,
              sp.season_rank
            FROM power_asset_event pae
            JOIN season_priority sp
              ON sp.season_id = pae.game_space_id
            WHERE pae.type = 'PHOTOGRAPH'
              AND EXISTS (
                SELECT 1
                FROM power_asset_detail_of_ntt_me pad
                WHERE pad.power_asset_id = pae.power_asset_id
              )
          ),
          first_hit AS (
            SELECT
              power_asset_id,
              MIN(season_rank) AS first_rank
            FROM eligible
            GROUP BY power_asset_id
          )
          SELECT
            sp.season_id,
            'NTT_ME' AS company_code,
            COUNT(*) AS cnt
          FROM first_hit fh
          JOIN season_priority sp
            ON sp.season_rank = fh.first_rank
          GROUP BY sp.season_id;
        `,
        database: "KpiDashboard",
        type: "list",
      }
    : {
        query: `
          SELECT
            pae.game_space_id AS season_id,
            'NTT_ME' AS company_code,
            COUNT(DISTINCT pae.power_asset_id) AS cnt
          FROM power_asset_event pae
          WHERE pae.type = 'PHOTOGRAPH'
            AND pae.game_space_id = '${sortedSeasonIds[0]}'
            AND EXISTS (
              SELECT 1
              FROM power_asset_detail_of_ntt_me pad
              WHERE pad.power_asset_id = pae.power_asset_id
            )
          GROUP BY pae.game_space_id;
        `,
        database: "KpiDashboard",
        type: "list",
      };

  return {
    seasonIds: sortedSeasonIds,
    seasonListQuery,
    seasonCountsQuery,
  };
};

const resolveRegularSeasonSelectionLabel = (selectedSeasonIds, selectedSeasonId) => {
  const uiSeasonIds = normalizeSelectionSeasonIds(selectedSeasonIds, selectedSeasonId);
  if (!uiSeasonIds.length) {
    return resolveRegularSeasonDisplayLabel(resolveGameSpaceId(selectedSeasonId));
  }

  return sortRegularSeasonGameSpaceIds(
    uiSeasonIds.map((seasonId) => resolveGameSpaceId(seasonId)).filter(Boolean)
  ).map((seasonId) => resolveRegularSeasonDisplayLabel(seasonId)).join(" + ");
};

const resolveMaxNumber = (gameSpaceId) => REGULAR_SEASON_MAX_BY_GAME_SPACE_ID[gameSpaceId] || 0;

const resolveSeasonPeriodLabel = (selectedSeasonId) => {
  const match = String(selectedSeasonId || "").match(/regular-season-(\d+)/);
  return match ? `RS${match[1]}` : String(selectedSeasonId || "");
};

const resolveRegularSeasonDisplayLabel = (gameSpaceId) => (
  REGULAR_SEASON_GAME_SPACE_ID_TO_LABEL[gameSpaceId]
  || resolveSeasonPeriodLabel(gameSpaceId)
  || String(gameSpaceId || "")
);

const normalizeSelectionSeasonIds = (selectedSeasonIds, selectedSeasonId) => {
  if (Array.isArray(selectedSeasonIds) && selectedSeasonIds.length > 0) {
    return selectedSeasonIds.filter(Boolean);
  }
  return selectedSeasonId ? [selectedSeasonId] : [];
};

const resolveSummaryScope = (seasonIds) => (seasonIds.length > 1 ? "MULTI" : "SINGLE");

const buildSummarySelectionKey = (seasonIds) => seasonIds
  .slice()
  .sort((left, right) => (REGULAR_SEASON_SELECTION_ORDER[left] ?? Number.MAX_SAFE_INTEGER) - (REGULAR_SEASON_SELECTION_ORDER[right] ?? Number.MAX_SAFE_INTEGER))
  .join("+");

const normalizeCompanyCode = (companyCode) => String(companyCode || "").replace(/-/g, "_").toUpperCase();

const resolveCompanyName = (companyCode) => {
  const normalized = normalizeCompanyCode(companyCode);
  return COMPANY_NAME_BY_CODE[normalized] || companyCode || "不明";
};

const resolveRegularSeasonLabel = (seasonId, seasonName) => {
  const explicitName = String(seasonName || "").trim();
  if (explicitName) {
    return explicitName;
  }
  const match = String(seasonId || "").match(/regular-season-(\d+)/);
  return match ? `レギュラーシーズン${match[1]}` : String(seasonId || "");
};

const emptyTrend = () => ({
  date: [],
  uniqueShots: [],
  reviewerOk: [],
  uniqueShooters: [],
});

const emptyKpis = () => ([
  {
    key: "uniqueShot",
    title: "ユニーク撮影本数",
    value: "--",
  },
  {
    key: "achievementRate",
    title: "撮影達成率",
    value: "--",
    subtitle: "対象 --本",
  },
  {
    key: "reviewOk",
    title: "レビューOK基数",
    value: "--",
    subtitle: "OK率 --%",
  },
  {
    key: "uniqueShooter",
    title: "ユニーク撮影者数",
    value: "--",
    subtitle: "累計参加者",
  },
  {
    key: "firstShooter",
    title: "初回撮影者数",
    value: "--",
    subtitle: "今期新規",
  },
]);

const emptySeasonChart = () => ({
  labels: [],
  series: [],
});

const emptyRegularSeasonPageData = (selectedSeasonId, selectedSeasonIds = selectedSeasonId ? [selectedSeasonId] : []) => ({
  selectedSeasonId,
  selectedSeasonIds,
  kpis: emptyKpis(),
  trend: emptyTrend(),
  trendLoading: true,
  companyRowsLoading: true,
  seasonChart: emptySeasonChart(),
  seasonChartLoading: true,
  companyRows: [],
});

const enumerateDaysJst = (startJst, endJst) => {
  const days = [];
  const start = moment.tz(startJst, "Asia/Tokyo").startOf("day");
  const end = moment.tz(endJst, "Asia/Tokyo").startOf("day");
  let current = start.clone();

  while (current.isSameOrBefore(end, "day")) {
    days.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  return days;
};

const normalizeDailyRows = (rows, dayKey = "day_jst", countKey = "cnt") => {
  const result = new Map();
  (rows || []).forEach((row) => {
    const rawDay = row[dayKey];
    const day = rawDay ? String(rawDay).slice(0, 10) : "";
    result.set(day, Number(row[countKey]) || 0);
  });
  return result;
};

const sumDailyRows = (rows, dayKey = "day_jst", countKeys = []) => {
  const result = new Map();
  (rows || []).forEach((row) => {
    const rawDay = row[dayKey];
    const day = rawDay ? String(rawDay).slice(0, 10) : "";
    if (!day) {
      return;
    }

    const current = result.get(day) || {};
    countKeys.forEach((countKey) => {
      current[countKey] = (Number(current[countKey]) || 0) + (Number(row[countKey]) || 0);
    });
    result.set(day, current);
  });
  return result;
};

const pickLatestRowsBySeasonAndCompany = (rows) => {
  const latestByKey = new Map();

  (rows || []).forEach((row) => {
    const seasonId = row.season_id;
    const companyCode = row.company_code;
    if (!seasonId || !companyCode || latestByKey.has(`${seasonId}:${companyCode}`)) {
      return;
    }

    latestByKey.set(`${seasonId}:${companyCode}`, row);
  });

  return Array.from(latestByKey.values());
};

const aggregateCompanyRowsByCode = (rows) => {
  const aggregated = new Map();

  (rows || []).forEach((row) => {
    const companyCode = row.company_code;
    if (!companyCode) {
      return;
    }

    const current = aggregated.get(companyCode) || {
      company_code: companyCode,
      unique_shots: 0,
      review_ok_count: 0,
    };

    current.unique_shots += Number(row.unique_shots) || 0;
    current.review_ok_count += Number(row.review_ok_count) || 0;
    aggregated.set(companyCode, current);
  });

  return Array.from(aggregated.values());
};

const normalizeRdsRegularSeasonTrendRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    target_date_jst: row?.[0]?.stringValue || null,
    unique_shot_count: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    unique_shooter_count: row?.[3]?.longValue ?? row?.[3]?.doubleValue ?? row?.[3]?.stringValue ?? null,
  }));
};

const normalizeRdsRegularSeasonCompanyTrendRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    target_date_jst: row?.[3]?.stringValue || null,
    unique_shot_count: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    unique_shooter_count: 0,
  }));
};

const normalizeRdsRegularSeasonCompanyRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    season_id: row?.[0]?.stringValue || null,
    company_code: row?.[1]?.stringValue || null,
    unique_shots: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    review_ok_count: row?.[3]?.longValue ?? row?.[3]?.doubleValue ?? row?.[3]?.stringValue ?? null,
    batch_day_jst: row?.[4]?.stringValue || null,
    batch_run_at_jst: row?.[5]?.stringValue || null,
  }));
};

const normalizeRdsRegularSeasonCompanyRowsRaw = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    company_code: row?.[0]?.stringValue || null,
    unique_shots: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
  }));
};

const pickLatestCompanyRows = (rows) => {
  const latestByCompany = new Map();

  (rows || []).forEach((row) => {
    const companyCode = row.company_code;
    if (!companyCode || latestByCompany.has(companyCode)) {
      return;
    }

    latestByCompany.set(companyCode, row);
  });

  return Array.from(latestByCompany.values());
};

const buildRegularSeasonCompanyRowsRawQuery = (gameSpaceIds) => {
  const gameSpaceIdList = gameSpaceIds.map((seasonId) => `'${seasonId}'`).join(", ");

  return {
    query: `
      SELECT
        'NTT_ME' AS company_code,
        (
          SELECT COUNT(*)
          FROM (
            SELECT DISTINCT pae.power_asset_id
            FROM (
              SELECT power_asset_id
              FROM power_asset_event FORCE INDEX (idx_power_asset_event_game_space_occurred_datetime)
              WHERE type = 'PHOTOGRAPH'
                AND game_space_id IN (${gameSpaceIdList})
            ) pae
            JOIN power_asset_detail_of_ntt_me pad
              ON pad.power_asset_id = pae.power_asset_id
          ) photo_assets
        ) AS unique_shots,
        (
          SELECT COUNT(*)
          FROM (
            SELECT DISTINCT rr.power_asset_id
            FROM (
              SELECT id, power_asset_id
              FROM review_request FORCE INDEX (game_space_id)
              WHERE game_space_id IN (${gameSpaceIdList})
            ) rr
            JOIN review_request_event rre FORCE INDEX (idx_review_request_event_request_type)
              ON rre.review_request_id = rr.id
             AND rre.type = 'APPROVED'
            JOIN power_asset_detail_of_ntt_me pad
              ON pad.power_asset_id = rr.power_asset_id
          ) review_assets
        ) AS review_ok_count;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const buildRegularSeasonSummaryKpis = ({ countDenchu, countReviewOk, countUniqueUser, countNewUser, maxNumber }) => {
  const achievementRate = maxNumber > 0 ? (countReviewOk / maxNumber) * 100 : 0;
  const okRate = countDenchu > 0 ? (countReviewOk / countDenchu) * 100 : 0;

  return [
    {
      key: "uniqueShot",
      title: "ユニーク撮影本数",
      value: formatNumber(countDenchu),
    },
    {
      key: "achievementRate",
      title: "撮影達成率",
      value: `${achievementRate.toFixed(1)}%`,
      subtitle: `対象 ${formatNumber(maxNumber)}本`,
    },
    {
      key: "reviewOk",
      title: "レビューOK基数",
      value: formatNumber(countReviewOk),
      subtitle: `OK率 ${okRate.toFixed(1)}%`,
    },
    {
      key: "uniqueShooter",
      title: "ユニーク撮影者数",
      value: formatNumber(countUniqueUser),
      subtitle: "累計参加者",
    },
    {
      key: "firstShooter",
      title: "初回撮影者数",
      value: formatNumber(countNewUser),
      subtitle: "今期新規",
    },
  ];
};

function* fetchRegularSeasonSummary(selectedSeasonId, selectedSeasonIds) {
  const jwtToken = yield call(getJwtToken);
  const seasonIds = normalizeSelectionSeasonIds(selectedSeasonIds, selectedSeasonId);
  const summaryScope = resolveSummaryScope(seasonIds);
  const selectionKey = buildSummarySelectionKey(seasonIds);

  const sqlQueryCountPhotoDenchu = {
    query: `
      SELECT
        target_count,
        unique_shots,
        review_ok_count,
        achievement_rate,
        unique_shooter_count,
        first_shooter_count
      FROM ${REGULAR_SEASON_OVERVIEW_SUMMARY_RDS_TABLE}
      WHERE summary_scope = '${summaryScope}'
        AND selection_key = '${selectionKey}'
      ORDER BY batch_day_jst DESC, batch_run_at_jst DESC, updated_at DESC
      LIMIT 1;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const summaryRows = yield call(
    callWithTimeoutAndRetry,
    getDataFromRDS,
    [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_RDS, jwtToken],
    SXI_HEAVY_RETRY
  );

  const summaryRow = normalizeRdsRegularSeasonSummaryRows(summaryRows)?.[0] || null;
  if (!summaryRow) {
    return {
      selectedSeasonId,
      kpis: emptyKpis(),
      summaryCounts: null,
    };
  }

  const targetCount = Number(summaryRow.target_count) || 0;
  const countDenchu = Number(summaryRow.unique_shots) || 0;
  const countReviewOk = Number(summaryRow.review_ok_count) || 0;
  const countUniqueUser = Number(summaryRow.unique_shooter_count) || 0;
  const countNewUser = Number(summaryRow.first_shooter_count) || 0;

  return {
    selectedSeasonId,
    kpis: buildRegularSeasonSummaryKpis({
      countDenchu,
      countReviewOk,
      countUniqueUser,
      countNewUser,
      maxNumber: targetCount,
    }),
    summaryCounts: {
      targetCount,
      countDenchu,
      countReviewOk,
      countUniqueUser,
      countNewUser,
      periodLabel: resolveRegularSeasonSelectionLabel(selectedSeasonIds, selectedSeasonId),
    },
  };
}

const normalizeRdsRegularSeasonSummaryRows = (rows) => {
  const parsedRows = JSON.parse(rows?.body || "[]");

  return parsedRows.map((row) => ({
    target_count: row?.[0]?.longValue ?? row?.[0]?.doubleValue ?? row?.[0]?.stringValue ?? null,
    unique_shots: row?.[1]?.longValue ?? row?.[1]?.doubleValue ?? row?.[1]?.stringValue ?? null,
    review_ok_count: row?.[2]?.longValue ?? row?.[2]?.doubleValue ?? row?.[2]?.stringValue ?? null,
    achievement_rate: row?.[3]?.longValue ?? row?.[3]?.doubleValue ?? row?.[3]?.stringValue ?? null,
    unique_shooter_count: row?.[4]?.longValue ?? row?.[4]?.doubleValue ?? row?.[4]?.stringValue ?? null,
    first_shooter_count: row?.[5]?.longValue ?? row?.[5]?.doubleValue ?? row?.[5]?.stringValue ?? null,
  }));
};

function* fetchRegularSeasonTrend(selectedSeasonId, selectedSeasonIds) {
  const jwtToken = yield call(getJwtToken);
  const gameSpaceIds = normalizeRegularSeasonGameSpaceIds(selectedSeasonIds, selectedSeasonId);
  if (!gameSpaceIds.length) {
    return emptyTrend();
  }
  const gameSpaceIdList = gameSpaceIds.map((seasonId) => `'${seasonId}'`).join(", ");

  const seasonRangeQuery = {
    query: `
      SELECT
        DATE_FORMAT(MIN(CONVERT_TZ(gt.start_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS start_jst,
        DATE_FORMAT(MAX(CONVERT_TZ(gt.end_datetime, 'UTC', 'Asia/Tokyo')), '%Y-%m-%d') AS end_jst
      FROM game_space gs
      JOIN game_term gt ON gt.id = gs.game_term_id
      WHERE gs.id IN (${gameSpaceIdList});
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const [seasonRangeRows] = yield all([
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonRangeQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
  ]);

  const seasonStartJst = seasonRangeRows?.[0]?.start_jst;
  const seasonEndJst = seasonRangeRows?.[0]?.end_jst;

  if (!seasonStartJst || !seasonEndJst) {
    return emptyTrend();
  }

  const todayJst = moment.tz("Asia/Tokyo").format("YYYY-MM-DD");
  const trendEndJst = moment.min(
    moment.tz(seasonEndJst, "Asia/Tokyo").startOf("day"),
    moment.tz(todayJst, "Asia/Tokyo").startOf("day")
  ).format("YYYY-MM-DD");

  const trendQuery = {
    query: `
      SELECT
        targetDateJst AS target_date_jst,
        uniqueShotCount AS unique_shot_count,
        reviewOkCount AS review_ok_count,
        uniqueShooterCount AS unique_shooter_count
      FROM ${REGULAR_SEASON_TREND_RDS_TABLE}
      WHERE seasonId IN (${gameSpaceIdList})
        AND companyCode = '${REGULAR_SEASON_TREND_COMPANY_CODE}'
        AND targetDateJst >= '${seasonStartJst}'
        AND targetDateJst <= '${trendEndJst}'
      ORDER BY targetDateJst ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const companyTrendQuery = {
    query: `
      SELECT
        seasonId AS season_id,
        companyCode AS company_code,
        uniqueShots AS unique_shots,
        reviewOkCount AS review_ok_count,
        batchDayJst AS batch_day_jst,
        batchRunAtJst AS batch_run_at_jst
      FROM ${REGULAR_SEASON_COMPANY_ACHIEVE_RDS_TABLE}
      WHERE seasonId IN (${gameSpaceIdList})
        AND companyCode = '${REGULAR_SEASON_TREND_COMPANY_CODE}'
        AND batchDayJst >= '${seasonStartJst}'
        AND batchDayJst <= '${trendEndJst}'
      ORDER BY seasonId ASC, companyCode ASC, batchDayJst DESC, batchRunAtJst DESC, createdAt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };

  const trendRows = yield call(
    callWithTimeoutAndRetry,
    getDataFromRDS,
    [trendQuery, ENDPOINT.GET_DATA_RDS, jwtToken],
    SXI_HEAVY_RETRY
  );

  const days = enumerateDaysJst(seasonStartJst, trendEndJst);
  const normalizedTrendRows = normalizeRdsRegularSeasonTrendRows(trendRows);
  const resolvedTrendRows = normalizedTrendRows.length > 0
    ? normalizedTrendRows
    : normalizeRdsRegularSeasonCompanyTrendRows(
      yield call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [companyTrendQuery, ENDPOINT.GET_DATA_RDS, jwtToken],
        SXI_HEAVY_RETRY
      )
    );
  const trendRowMap = sumDailyRows(resolvedTrendRows, "target_date_jst", [
    "unique_shot_count",
    "review_ok_count",
    "unique_shooter_count",
  ]);

  return {
    date: days.map((day) => moment(day).format("M/D")),
    uniqueShots: days.map((day) => Number(trendRowMap.get(day)?.unique_shot_count) || 0),
    reviewerOk: days.map((day) => Number(trendRowMap.get(day)?.review_ok_count) || 0),
    uniqueShooters: days.map((day) => Number(trendRowMap.get(day)?.unique_shooter_count) || 0),
  };
}

function* fetchRegularSeasonCompanyRows(selectedSeasonId, selectedSeasonIds) {
  const jwtToken = yield call(getJwtToken);
  const gameSpaceIds = normalizeRegularSeasonGameSpaceIds(selectedSeasonIds, selectedSeasonId);
  if (!gameSpaceIds.length) {
    return [];
  }
  const gameSpaceIdList = gameSpaceIds.map((seasonId) => `'${seasonId}'`).join(", ");
  const maxNumber = gameSpaceIds.reduce((max, seasonId) => Math.max(max, resolveMaxNumber(seasonId)), 0);
  const periodLabel = resolveRegularSeasonSelectionLabel(selectedSeasonIds, selectedSeasonId);
  const isMultiSeason = gameSpaceIds.length > 1;

  const companyRowsQuery = isMultiSeason
    ? buildRegularSeasonCompanyRowsRawQuery(gameSpaceIds)
    : {
        query: `
          SELECT
            seasonId AS season_id,
            companyCode AS company_code,
            uniqueShots AS unique_shots,
            reviewOkCount AS review_ok_count,
            batchDayJst AS batch_day_jst,
            batchRunAtJst AS batch_run_at_jst
          FROM ${REGULAR_SEASON_COMPANY_ACHIEVE_RDS_TABLE}
          WHERE seasonId IN (${gameSpaceIdList})
          ORDER BY seasonId ASC, companyCode ASC, batchDayJst DESC, batchRunAtJst DESC, createdAt DESC;
        `,
        database: "KpiDashboard",
        type: "list",
      };

  const companyRowsRows = yield call(
    callWithTimeoutAndRetry,
    isMultiSeason ? getDataFromSxi : getDataFromRDS,
    [companyRowsQuery, isMultiSeason ? ENDPOINT.GET_DATA_SXI_DENCHU : ENDPOINT.GET_DATA_RDS, jwtToken],
    SXI_HEAVY_RETRY
  );

  const normalizedRows = isMultiSeason
    ? normalizeRdsRegularSeasonCompanyRowsRaw(companyRowsRows)
    : normalizeRdsRegularSeasonCompanyRows(companyRowsRows);
  const rowsForAggregation = isMultiSeason
    ? normalizedRows
    : pickLatestRowsBySeasonAndCompany(normalizedRows);
  const aggregatedRows = aggregateCompanyRowsByCode(rowsForAggregation).sort((a, b) => {
    const reviewDiff = (Number(b.review_ok_count) || 0) - (Number(a.review_ok_count) || 0);
    if (reviewDiff !== 0) {
      return reviewDiff;
    }

    const shotDiff = (Number(b.unique_shots) || 0) - (Number(a.unique_shots) || 0);
    if (shotDiff !== 0) {
      return shotDiff;
    }

    return String(a.company_code || "").localeCompare(String(b.company_code || ""));
  });

  return aggregatedRows.map((row) => {
    const reviewOkCount = Number(row.review_ok_count) || 0;
    const rate = maxNumber > 0 ? (reviewOkCount / maxNumber) * 100 : 0;

    return {
      companyCode: row.company_code,
      companyName: resolveCompanyName(row.company_code),
      rate,
      progress: rate,
      period: periodLabel,
    };
  });
}

function* fetchRegularSeasonSeasonChart(selectedSeasonId, selectedSeasonIds) {
  const jwtToken = yield call(getJwtToken);
  const { seasonIds, seasonListQuery, seasonCountsQuery } = buildRegularSeasonSeasonChartQueries({
    selectedSeasonId,
    selectedSeasonIds,
  });

  if (!seasonIds.length || !seasonListQuery || !seasonCountsQuery) {
    return {
      labels: [],
      series: [],
    };
  }

  const [seasonListRows, seasonCountRows] = yield all([
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonListQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
    call(
      callWithTimeoutAndRetry,
      getDataFromSxi,
      [seasonCountsQuery, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    ),
  ]);

  const seasons = (seasonListRows || []).map((row) => ({
    seasonId: row.season_id,
    seasonLabel: resolveRegularSeasonDisplayLabel(row.season_id)
      || resolveRegularSeasonLabel(row.season_id, row.season_name),
  }));

  if (!seasons.length) {
    return {
      labels: [],
      series: [],
    };
  }

  const seasonLabelMap = new Map(seasons.map((item) => [item.seasonId, item.seasonLabel]));
  const seasonOrder = seasons.map((item) => item.seasonId);
  const countMap = new Map();

  (seasonCountRows || []).forEach((row) => {
    const seasonId = row.season_id;
    const companyCode = normalizeCompanyCode(row.company_code);
    const cnt = Number(row.cnt) || 0;
    countMap.set(`${seasonId}:${companyCode}`, cnt);
  });

  const labels = seasonOrder.map((seasonId) => seasonLabelMap.get(seasonId) || seasonId);
  const series = COMPANY_SERIES_ORDER
    .filter((companyCode) => seasonOrder.some((seasonId) => (countMap.get(`${seasonId}:${companyCode}`) || 0) > 0))
    .map((companyCode) => ({
      name: resolveCompanyName(companyCode),
      data: seasonOrder.map((seasonId) => countMap.get(`${seasonId}:${companyCode}`) || 0),
    }));

  return {
    labels,
    series,
  };
}

function* fetchAndStoreRegularSeasonSection(
  sectionKey,
  fetcher,
  selectedSeasonId,
  fallbackValue,
  errorLabel,
  extractValue = (value) => value,
  loadingKey,
  ...fetchArgs
) {
  try {
    if (loadingKey) {
      yield put(setPicTreeRegularSeasonPageData({
        [loadingKey]: true,
      }));
    }
    const rawValue = yield call(fetcher, selectedSeasonId, ...fetchArgs);
    const value = extractValue(rawValue);
    yield put(setPicTreeRegularSeasonPageData({
      [sectionKey]: value,
    }));
    if (loadingKey) {
      yield put(setPicTreeRegularSeasonPageData({
        [loadingKey]: false,
      }));
    }
    return value;
  } catch (error) {
    const payload = {
      [sectionKey]: fallbackValue,
    };
    if (loadingKey) {
      payload[loadingKey] = false;
    }
    yield put(setPicTreeRegularSeasonPageData(payload));
    return fallbackValue;
  }
}

function* fetchRegularSeasonBottomSections(selectedSeasonId, selectedSeasonIds) {
  yield all([
    call(
      fetchAndStoreRegularSeasonSection,
      "companyRows",
      fetchRegularSeasonCompanyRows,
      selectedSeasonId,
      [],
      "company rows",
      (value) => value,
      "companyRowsLoading",
      selectedSeasonIds
    ),
    call(
      fetchAndStoreRegularSeasonSection,
      "seasonChart",
      fetchRegularSeasonSeasonChart,
      selectedSeasonId,
      emptySeasonChart(),
      "season chart",
      (value) => value,
      "seasonChartLoading",
      selectedSeasonIds
    ),
  ]);
}

const buildRegularSeasonMultiCompanyRowsFromSummary = (summaryCounts) => {
  if (!summaryCounts) {
    return [];
  }

  const rate = summaryCounts.targetCount > 0
    ? (Number(summaryCounts.countReviewOk) || 0) / summaryCounts.targetCount * 100
    : 0;

  return [{
    companyCode: "NTT_ME",
    companyName: resolveCompanyName("NTT_ME"),
    rate,
    progress: rate,
    period: summaryCounts.periodLabel || "MULTI",
  }];
};

export function* handleRegularSeasonPageData(action) {
  try {
    yield put(setPicTreeRegularSeasonPageLoading(true));
    yield put(setPageTopLoader(true));
    const selectedSeasonId = action?.payload?.selectedSeasonId || "regular-season-1";
    const selectedSeasonIds = normalizeSelectionSeasonIds(action?.payload?.selectedSeasonIds, selectedSeasonId);
    yield put(setPicTreeRegularSeasonPageData(emptyRegularSeasonPageData(selectedSeasonId, selectedSeasonIds)));

    const [summaryResult, trendResult] = yield all([
      call(fetchRegularSeasonSummary, selectedSeasonId, selectedSeasonIds),
      call(fetchRegularSeasonTrend, selectedSeasonId, selectedSeasonIds),
    ]);

    yield put(setPicTreeRegularSeasonPageData({
      kpis: summaryResult.kpis || emptyKpis(),
      trend: trendResult || emptyTrend(),
      trendLoading: false,
    }));

    if (selectedSeasonIds.length > 1) {
      yield put(setPicTreeRegularSeasonPageData({
        companyRows: buildRegularSeasonMultiCompanyRowsFromSummary(summaryResult.summaryCounts),
        companyRowsLoading: false,
        seasonChartLoading: true,
      }));
      yield call(
        fetchAndStoreRegularSeasonSection,
        "seasonChart",
        fetchRegularSeasonSeasonChart,
        selectedSeasonId,
        emptySeasonChart(),
        "season chart",
        (value) => value,
        "seasonChartLoading",
        selectedSeasonIds
      );
    } else {
      yield call(
        fetchAndStoreRegularSeasonSection,
        "companyRows",
        fetchRegularSeasonCompanyRows,
        selectedSeasonId,
        [],
        "company rows",
        (value) => value,
        "companyRowsLoading",
        selectedSeasonIds
      );
      yield call(
        fetchAndStoreRegularSeasonSection,
        "seasonChart",
        fetchRegularSeasonSeasonChart,
        selectedSeasonId,
        emptySeasonChart(),
        "season chart",
        (value) => value,
        "seasonChartLoading",
        selectedSeasonIds
      );
    }
  } catch (error) {
    // no-op
  } finally {
    yield put(setPageTopLoader(false));
    yield put(setPicTreeRegularSeasonPageLoading(false));
  }
}

export default function* pictreeRegularSeasonSaga() {
  yield takeEvery(PICTREE.GET_REGULAR_SEASON_PAGE_DATA, handleRegularSeasonPageData);
}
